import React, { useEffect, useState } from 'react';
import { attendanceApi } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import { Input } from '../../components/ui/input';
import { LogIn, LogOut, MapPin, ExternalLink } from 'lucide-react';

const AttendanceMonitorPage = () => {
  const { toast } = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [view, setView] = useState('ozet'); // ozet | hareketler
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await attendanceApi.list({ date });
      setRows(res.data?.data || []);
      setSummary(res.data?.summary || []);
    } catch {
      toast({ title: 'Hata', description: 'Kayıtlar alınamadı', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const inCount = rows.filter((r) => r.type === 'in').length;
  const outCount = rows.filter((r) => r.type === 'out').length;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Yoklama Takibi</h1>
          <p className="text-sm text-slate-500 mt-1">QR ile alınan giriş/çıkış kayıtları (konum + IP)</p>
        </div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-44" />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Toplam Hareket', value: rows.length, color: 'text-slate-800' },
          { label: 'Giriş', value: inCount, color: 'text-emerald-600' },
          { label: 'Çıkış', value: outCount, color: 'text-slate-500' },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-400">{c.label}</p>
            <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Görünüm seçimi */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setView('ozet')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${view === 'ozet' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
        >
          Çalışma Saatleri (Puantaj)
        </button>
        <button
          onClick={() => setView('hareketler')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${view === 'hareketler' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
        >
          Hareketler
        </button>
      </div>

      {/* Personel bazlı çalışma saati özeti */}
      {view === 'ozet' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                  <th className="px-4 py-3 font-medium">Personel</th>
                  <th className="px-4 py-3 font-medium">Ekip</th>
                  <th className="px-4 py-3 font-medium">İlk Giriş</th>
                  <th className="px-4 py-3 font-medium">Son Çıkış</th>
                  <th className="px-4 py-3 font-medium">Çalışılan Saat</th>
                  <th className="px-4 py-3 font-medium">Durum</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
                ) : summary.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Bu tarihte kayıt yok</td></tr>
                ) : summary.map((s) => (
                  <tr key={s.personnel_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {s.personnel_name}{s.sicil_no && <span className="text-slate-400 font-normal"> · {s.sicil_no}</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{s.team_code || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{s.first_in ? new Date(s.first_in).toLocaleTimeString('tr-TR') : '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{s.last_out ? new Date(s.last_out).toLocaleTimeString('tr-TR') : '—'}</td>
                    <td className="px-4 py-3 font-bold text-emerald-600">{s.worked_hours} s</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.currently_in ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {s.currently_in ? 'İçeride' : 'Tamamlandı'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'hareketler' && (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="px-4 py-3 font-medium">Saat</th>
                <th className="px-4 py-3 font-medium">Personel</th>
                <th className="px-4 py-3 font-medium">Tip</th>
                <th className="px-4 py-3 font-medium">Nokta</th>
                <th className="px-4 py-3 font-medium">Konum</th>
                <th className="px-4 py-3 font-medium">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Bu tarihte kayıt yok</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-600">{new Date(r.created_at).toLocaleTimeString('tr-TR')}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {r.personnel_name}
                    {r.sicil_no && <span className="text-slate-400 font-normal"> · {r.sicil_no}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      r.type === 'in' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {r.type === 'in' ? <LogIn className="h-3 w-3" /> : <LogOut className="h-3 w-3" />}
                      {r.type === 'in' ? 'Giriş' : 'Çıkış'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{r.team_code || (r.scope === 'idari' ? 'İdari' : r.scope)}</td>
                  <td className="px-4 py-3">
                    {r.latitude && r.longitude ? (
                      <a
                        href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <MapPin className="h-3.5 w-3.5" /> Haritada <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{r.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default AttendanceMonitorPage;
